import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Copy, Check } from "lucide-react";
import { useGetTransactionDetailQuery } from "@/services/cav-offline-sync";
import { Skeleton } from "@/components/ui/skeleton";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { formatToBRL } from "@/utils/formatToMoney";
import { formatDateTime2 } from "@/utils/format-date";
import { statusConfig } from "../constants";

interface TransactionDetailContentProps {
  store: number;
  docCode: number;
}

const DetailField = ({
  label,
  value,
}: {
  label: string;
  value: string | number | null;
}) => (
  <div className="flex flex-col">
    <span className="text-sm text-gray-500">{label}</span>
    <span className="font-medium">{value ?? "-"}</span>
  </div>
);

export function TransactionDetailContent({
  store,
  docCode,
}: TransactionDetailContentProps) {
  const { handleModal } = useDialogModal();
  const [copied, setCopied] = useState(false);

  const { data, isLoading, error } = useGetTransactionDetailQuery(
    { store, docCode },
    { skip: false }
  );

  const handleCopy = async () => {
    if (data?.processingResult) {
      await navigator.clipboard.writeText(data.processingResult);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    handleModal({ isOpen: false, element: null, title: "" });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
        <Skeleton className="h-24 w-full" />
        <div className="flex justify-end">
          <Button variant="outline" onClick={handleClose}>
            Fechar
          </Button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="text-center py-8 text-gray-500">
          Transacao nao encontrada
        </div>
        <div className="flex justify-end">
          <Button variant="outline" onClick={handleClose}>
            Fechar
          </Button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex justify-end">
        <Button variant="outline" onClick={handleClose}>
          Fechar
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <DetailField label="Loja" value={data.store} />
        <DetailField label="Documento" value={data.docCode} />
        <DetailField label="Caixa" value={data.register} />
        <DetailField label="NSU Origem" value={data.nsu} />
        <DetailField label="Valor" value={data.value !== null ? formatToBRL(data.value) : "-"} />
        <DetailField label="Parcelas" value={data.installments} />
        <DetailField
          label="Terminal Requisitante"
          value={data.requestingTerminal}
        />
        <DetailField
          label="Data/Hora Terminal"
          value={formatDateTime2(data.terminalDate)}
        />
        <DetailField label="ID Portador" value={data.cardholderId} />
        <div className="flex flex-col">
          <span className="text-sm text-gray-500">Status</span>
          <div className="mt-1">
            <Badge
              className={`${statusConfig[data.status]?.className || ""} cursor-default`}
            >
              {statusConfig[data.status]?.label || data.status}
            </Badge>
          </div>
        </div>
      </div>

      {data.processingResult && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Mensagem de Erro</span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopy}
              className="h-7"
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4 mr-1" />
                  Copiado!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-1" />
                  Copiar
                </>
              )}
            </Button>
          </div>
          <div className="bg-gray-50 border rounded-md p-3 max-h-40 overflow-y-auto">
            <pre className="text-sm whitespace-pre-wrap font-mono break-words">
              {data.processingResult}
            </pre>
          </div>
        </div>
      )}

      <DetailField
        label="Data Processamento"
        value={formatDateTime2(data.processingDate)}
      />

      <div className="flex justify-end">
        <Button variant="outline" onClick={handleClose}>
          Fechar
        </Button>
      </div>
    </div>
  );
}
